import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import CategoryTable from "../components/CategoryTable";

import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import ErrorMessage from "../../../components/common/ErrorMessage";

import { useCategories } from "../hooks/useCategories";

import { useAuth } from "../../auth/hooks/useAuth";

import "../styles/categories.css";
import "../styles/category-table.css";

const CategoriesPage = () => {
  const navigate = useNavigate();

  const { user } = useAuth();

  const {
    categories,
    loading,
    error,
    updateCategory,
    deleteCategory
  } = useCategories({
    activeOnly: false
  });

  const [search, setSearch] =
    useState("");

  const [actionLoading, setActionLoading] =
    useState(false);


  /*
  |--------------------------------------------------------------------------
  | ADMIN CHECK
  |--------------------------------------------------------------------------
  */

  const isAdmin =
    user?.roles?.includes("ADMIN");


  /*
  |--------------------------------------------------------------------------
  | FILTER
  |--------------------------------------------------------------------------
  */

  const filteredCategories =
    useMemo(() => {
      const value =
        search
          .trim()
          .toLowerCase();

      if (!value) {
        return categories;
      }

      return categories.filter(
        (category) => {
          const name =
            category.name
              ?.toLowerCase() || "";

          const slug =
            category.slug
              ?.toLowerCase() || "";

          const description =
            category.description
              ?.toLowerCase() || "";

          return (
            name.includes(value) ||
            slug.includes(value) ||
            description.includes(value)
          );
        }
      );
    }, [categories, search]);


  /*
  |--------------------------------------------------------------------------
  | CREATE
  |--------------------------------------------------------------------------
  */

  const handleCreate = () => {
    navigate("/categories/create");
  };


  /*
  |--------------------------------------------------------------------------
  | VIEW
  |--------------------------------------------------------------------------
  */

  const handleView = (category) => {
    navigate(
      `/categories/${category.id}`
    );
  };


  /*
  |--------------------------------------------------------------------------
  | EDIT
  |--------------------------------------------------------------------------
  */

  const handleEdit = (category) => {
    navigate(
      `/categories/${category.id}/edit`
    );
  };


  /*
  |--------------------------------------------------------------------------
  | TOGGLE STATUS
  |--------------------------------------------------------------------------
  */

  const handleToggleStatus =
    async (category) => {
      const nextStatus =
        !category.is_active;

      const action =
        nextStatus
          ? "activate"
          : "deactivate";

      const confirmed =
        window.confirm(
          `Are you sure you want to ${action} "${category.name}"?`
        );

      if (!confirmed) {
        return;
      }

      try {
        setActionLoading(true);

        await updateCategory(
          category.id,
          {
            isActive: nextStatus
          }
        );
      } catch (error) {
        window.alert(
          error?.response?.data
            ?.message ||
            `Unable to ${action} category.`
        );
      } finally {
        setActionLoading(false);
      }
    };


  /*
  |--------------------------------------------------------------------------
  | DELETE
  |--------------------------------------------------------------------------
  */

  const handleDelete =
    async (category) => {
      const confirmed =
        window.confirm(
          `Are you sure you want to permanently delete "${category.name}"?`
        );

      if (!confirmed) {
        return;
      }

      try {
        setActionLoading(true);

        await deleteCategory(
          category.id
        );
      } catch (error) {
        window.alert(
          error?.response?.data
            ?.message ||
            "Unable to delete category."
        );
      } finally {
        setActionLoading(false);
      }
    };


  /*
  |--------------------------------------------------------------------------
  | SUMMARY
  |--------------------------------------------------------------------------
  */

  const totalCategories =
    categories.length;

  const activeCategories =
    categories.filter(
      (category) =>
        category.is_active
    ).length;

  const inactiveCategories =
    categories.filter(
      (category) =>
        !category.is_active
    ).length;


  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (
    <div className="categories-page">

      {/* HEADER */}

      <div className="categories-header">

        <div className="categories-header-content">

          <h1>
            Categories
          </h1>

          <p>
            Manage categories used to organize your offerings.
          </p>

        </div>

        {isAdmin && (
          <Button
            onClick={handleCreate}
            className="categories-create-button"
          >
            + Create category
          </Button>
        )}

      </div>


      {/* ERROR */}

      {error && (
        <div className="categories-error">
          <ErrorMessage
            message={
              typeof error === "string"
                ? error
                : error?.response?.data
                    ?.message ||
                  "Unable to load categories."
            }
          />
        </div>
      )}


      {/* SEARCH */}

      <div className="categories-search-card">

        <Input
          label="Search categories"
          placeholder="Search by name, slug or description..."
          value={search}
          onChange={(event) =>
            setSearch(
              event.target.value
            )
          }
        />

      </div>


      {/* SUMMARY */}

      <div className="categories-summary">

        <div className="categories-summary-card">

          <p className="categories-summary-label">
            Total categories
          </p>

          <p className="categories-summary-value">
            {totalCategories}
          </p>

        </div>


        <div className="categories-summary-card">

          <p className="categories-summary-label">
            Active categories
          </p>

          <p className="categories-summary-value active">
            {activeCategories}
          </p>

        </div>


        <div className="categories-summary-card">

          <p className="categories-summary-label">
            Inactive categories
          </p>

          <p className="categories-summary-value inactive">
            {inactiveCategories}
          </p>

        </div>

      </div>


      {/* TABLE */}

      <CategoryTable
        categories={filteredCategories}
        loading={
          loading ||
          actionLoading
        }
        onView={handleView}
        onEdit={handleEdit}
        onToggleStatus={
          handleToggleStatus
        }
        onDelete={
          isAdmin
            ? handleDelete
            : null
        }
      />

    </div>
  );
};

export default CategoriesPage;